import { useState, useCallback, useRef, Dispatch, SetStateAction } from 'react';


export interface Shape {
  id: string;
  type: 'rect' | 'quad' | 'circle' | 'line' | 'text' | 'pen' | 'bezier';
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  points?: number[];
  closed?: boolean;
  text?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  rotation?: number;
  scaleX?: number;
  scaleY?: number;
  fontSize?: number;
  tension?: number;
  className?: string;
  lineCap?: 'butt' | 'round' | 'square';
  lineJoin?: 'round' | 'bevel' | 'miter';
}


export type BezierShapeRecord = Record<string, unknown> & { id: string };


interface HistoryEntry {
  shapes: Shape[];
  bezierShapes: BezierShapeRecord[];
}

export interface UseCanvasHistoryReturn {
  /** Current live shapes — read these in the component */
  shapes: Shape[];
  /** Current live bezier shapes — read these in the component */
  bezierShapes: BezierShapeRecord[];

  /**
   * Live-only setter for shapes.
   * Use during drag / draw preview — does NOT write a history entry.
   */
  setShapesLive: Dispatch<SetStateAction<Shape[]>>;

  /**
   * Live-only setter for bezierShapes.
   * Use during drag / draw preview — does NOT write a history entry.
   */
  setBezierShapesLive: Dispatch<SetStateAction<BezierShapeRecord[]>>;

  /**
   * Push a committed snapshot onto the history stack.
   * Call this on every discrete action completion:
   *   mouseUp, delete, transform end, color change, layer reorder, etc.
   */
  commitState: (nextShapes: Shape[], nextBezierShapes: BezierShapeRecord[]) => void;

  undo: () => void;

  redo: () => void;

  canUndo: boolean;
  canRedo: boolean;
}


const MAX_HISTORY = 80;


/**
 * useCanvasHistory
 *
 * Splits canvas state into:
 *   • Live state   — updated freely during drag / draw / hover (never writes history)
 *   • Committed state — snapshot pushed on discrete action completion
 *
 * Workflow:
 *   // During drag (mousemove) — live only, no undo entry:
 *   setShapesLive(updatedShapes);
 *
 *   // On mouseUp / delete / property change — commit one undo step:
 *   commitState(finalShapes, finalBezierShapes);
 */
export function useCanvasHistory(
  initialShapes: Shape[],
  initialBezierShapes: BezierShapeRecord[],
): UseCanvasHistoryReturn {

  const [shapes, setShapesLive] = useState<Shape[]>(initialShapes);
  const [bezierShapes, setBezierShapesLive] = useState<BezierShapeRecord[]>(initialBezierShapes);

  const history = useRef<HistoryEntry[]>([
    {
      shapes: deepClone(initialShapes),
      bezierShapes: deepClone(initialBezierShapes),
    },
  ]);
  const cursor = useRef<number>(0);

  const [canUndo, setCanUndo] = useState<boolean>(false);
  const [canRedo, setCanRedo] = useState<boolean>(false);

  // Commit a history snapshot for undo/redo.
  // Each snapshot clones both shape arrays so live updates do not mutate history entries.
  const commitState = useCallback(
    (nextShapes: Shape[], nextBezierShapes: BezierShapeRecord[]) => {
      // Truncate redo stack — new action after undo clears the future
      const truncated = history.current.slice(0, cursor.current + 1);

      const entry: HistoryEntry = {
        shapes: deepClone(nextShapes),
        bezierShapes: deepClone(nextBezierShapes),
      };

      truncated.push(entry);

      if (truncated.length > MAX_HISTORY) {
        truncated.splice(0, truncated.length - MAX_HISTORY);
      }

      history.current = truncated;
      cursor.current = truncated.length - 1;

      setShapesLive(nextShapes);
      setBezierShapesLive(nextBezierShapes);

      setCanUndo(cursor.current > 0);
      setCanRedo(false);
    },
    [],
  );

  const undo = useCallback(() => {
    if (cursor.current <= 0) return;

    cursor.current -= 1;
    const entry = history.current[cursor.current];

    setShapesLive(deepClone(entry.shapes));
    setBezierShapesLive(deepClone(entry.bezierShapes));

    setCanUndo(cursor.current > 0);
    setCanRedo(cursor.current < history.current.length - 1);
  }, []);

  const redo = useCallback(() => {
    if (cursor.current >= history.current.length - 1) return;

    cursor.current += 1;
    const entry = history.current[cursor.current];

    setShapesLive(deepClone(entry.shapes));
    setBezierShapesLive(deepClone(entry.bezierShapes));

    setCanUndo(cursor.current > 0);
    setCanRedo(cursor.current < history.current.length - 1);
  }, []);

  return {
    shapes,
    bezierShapes,
    setShapesLive,
    setBezierShapesLive,
    commitState,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}


function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}